import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  CommissioningDeviceAssignHeatingCircuitParams,
  CommissioningRemoteDeviceAssignHeatingCircuitParams,
  CommissioningStore,
  DeviceAssignmentType,
  InstallationFeaturesStore,
  RemoteDeviceType,
} from '@store';
import { catchError, filter, take, takeUntil, timeout } from 'rxjs/operators';
import {
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  heatingCircuitRemoteRegex,
  IsEnabledProperty,
} from '../../../../../../projects/client';
import { InstallationService } from '../../../../services/installation.service';
import { ModalAction } from '../commissioning.model';
import { CommissioningService } from '../commissioning.service';
import { combineLatest, EMPTY, Subject } from 'rxjs';

export enum AssignHcState {
  Loading,
  AssignHc,
  ErrorNoHeatingCircuits,
}

@Component({
  selector: 'app-commissioning-assign-heating-circuit-modal',
  templateUrl: './commissioning-assign-heating-circuit-modal.component.html',
  styleUrls: ['./commissioning-assign-heating-circuit-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissioningAssignHeatingCircuitModalComponent implements OnInit, OnDestroy {
  AssignHcState = AssignHcState;
  readonly featurePropertyKey = FeaturePropertyKey;
  assignHcStateLoadingState = AssignHcState.Loading;

  allCircuits: Feature<IsEnabledProperty, never>;
  remoteCircuits: Feature<IsEnabledProperty, never>[];
  selectedFeatureOption: Feature<never, never> | null = null;
  selectedNumericOption: number | string | null = null;
  deviceType: RemoteDeviceType;
  remoteDeviceTypeValues = RemoteDeviceType;
  private pairedDeviceId: string;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    public installationService: InstallationService,
    public commissioningService: CommissioningService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private commissioningStore: CommissioningStore,
    private cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<CommissioningAssignHeatingCircuitModalComponent>
  ) {}

  ngOnInit(): void {
    combineLatest([this.commissioningStore.deviceType$, this.commissioningStore.pairedDeviceId$])
      .pipe(take(1), takeUntil(this.ngUnsubscribe$))
      .subscribe(([deviceType, pairedDeviceId]) => {
        this.deviceType = deviceType;
        this.pairedDeviceId = pairedDeviceId;

        this.dialogRef.updateSize('768px');
        this.dialogRef.addPanelClass('commissioning-modal');

        this.selectedFeatureOption = null;
        this.selectedNumericOption = null;

        if (deviceType === RemoteDeviceType.VITOTROL_300E) {
          this.loadRemoteCircuits();
        } else {
          this.loadAllCircuits();
        }
      });
  }

  private loadRemoteCircuits(): void {
    this.assignHcStateLoadingState = AssignHcState.Loading;
    this.installationFeaturesStore
      .selectAllInstallationDeviceRemoteFeatures(
        heatingCircuitRemoteRegex,
        this.pairedDeviceId,
        true
      )
      .pipe(
        timeout(5000),
        filter((entry) => !!entry && entry.length > 0),
        take(1),
        takeUntil(this.ngUnsubscribe$),
        catchError(() => {
          this.assignHcStateLoadingState = AssignHcState.ErrorNoHeatingCircuits;
          this.cdr.markForCheck();

          return EMPTY;
        })
      )
      .subscribe((heatingCircuits: Feature<IsEnabledProperty, never>[]) => {
        this.remoteCircuits = heatingCircuits;
        this.assignHcStateLoadingState = AssignHcState.AssignHc;
        this.cdr.markForCheck();
      });
  }

  private loadAllCircuits(): void {
    this.assignHcStateLoadingState = AssignHcState.Loading;
    this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.circuits)
      .pipe(
        timeout(5000),
        filter((entry) => !!entry),
        take(1),
        takeUntil(this.ngUnsubscribe$),
        catchError(() => {
          this.assignHcStateLoadingState = AssignHcState.ErrorNoHeatingCircuits;
          this.cdr.markForCheck();

          return EMPTY;
        })
      )
      .subscribe((circuits: Feature<IsEnabledProperty, never>) => {
        this.allCircuits = circuits;
        this.assignHcStateLoadingState = AssignHcState.AssignHc;
        this.cdr.markForCheck();
      });
  }

  onHcOptionSelect(hcOption: number | string | Feature<never, never>): void {
    if (typeof hcOption === 'number') {
      this.selectedNumericOption = hcOption;
      this.selectedFeatureOption = null;
    } else if (typeof hcOption === 'string') {
      this.selectedNumericOption = parseInt(hcOption);
      this.selectedFeatureOption = null;
    } else {
      this.selectedFeatureOption = hcOption;
      this.selectedNumericOption = null;
    }
  }

  navigateBack(): void {
    this.commissioningService.modalAction$.next(ModalAction.PreviousStep);
  }

  navigateForward(): void {
    if (this.deviceType === RemoteDeviceType.VITOTROL_300E) {
      this.updateFeatureForVitotrol300E();
    } else {
      this.assignHeatingCircuit();
    }

    this.commissioningService.modalAction$.next(ModalAction.NextStep);
  }

  updateFeatureForVitotrol300E(): void {
    if (this.selectedFeatureOption) {
      const assignedHC: CommissioningRemoteDeviceAssignHeatingCircuitParams = {
        type: DeviceAssignmentType.RemoteHC,
        deviceId: this.pairedDeviceId,
        circuitId: `${this.selectedFeatureOption.deviceId}`,
      };
      this.commissioningStore.remoteDeviceAssignHeatingCircuit(assignedHC);
    }
  }

  private assignHeatingCircuit(): void {
    if (this.selectedNumericOption !== null) {
      this.commissioningStore.pairedDeviceId$.pipe(take(1)).subscribe((pairedDeviceId) => {
        const assignedHC: CommissioningDeviceAssignHeatingCircuitParams = {
          type: DeviceAssignmentType.Hc,
          deviceId: pairedDeviceId,
          circuitId: `${this.selectedNumericOption}`,
        };

        this.commissioningStore.deviceAssignHeatingCircuit(assignedHC);
      });
    }
  }

  canNavigateForward(): boolean {
    return (
      this.selectedFeatureOption !== null ||
      this.selectedNumericOption !== null ||
      this.assignHcStateLoadingState === AssignHcState.ErrorNoHeatingCircuits ||
      this.deviceType === RemoteDeviceType.VITOTROL_300E
    );
  }

  closeDialog(): void {
    this.commissioningService.modalAction$.next(ModalAction.Close);
  }
  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
